import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery, useSubscription } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { withRouter, RouteComponentProps } from "react-router";
import { RootStoreContext } from "src/stores/RootStore";
import { Route } from "src/config/routes";
import {
    GET_UMS_COPY_CUSTOMER_PROCESSES_OVERVIEW_PAGED,
    SUBSCRIBE_UMS_COPY_CUSTOMER_PROCESSES_OVERVIEW_PAGED
} from "src/api/customers";
import * as Screens from "src/screens";
import { CopyCustomer } from "src/screens/dashboard/customers/overview/additional-actions/CopyCustomer";
import CopiedCustomerList from "./CopiedCustomerList";
import {
    GetUmsCopyCustomerProcessesOverviewPaged,
    GetUmsCopyCustomerProcessesOverviewPagedVariables
} from "src/api/generated/GetUmsCopyCustomerProcessesOverviewPaged";
import {
    SubscribeUmsCopyCustomerProcessesOverviewPaged,
    SubscribeUmsCopyCustomerProcessesOverviewPagedVariables
} from "src/api/generated/SubscribeUmsCopyCustomerProcessesOverviewPaged";
import styles from "./CustomersCopied.module.css";

interface ICopiedCustomerListItem {
    id: string;
    name: string | null;
    number: string | null;
    copyStatus: 1 | 2 | 3;
    copyProgress: number;
    copiedBy: string;
}

const ITEMS_PER_PAGE = 15;
const SEARCH_DEBOUNCE_DELAY_MS = 300;
const RUNNING_COPY_STATUS = 1;

interface ICopyCustomerProcessUpdate {
    status: 1 | 2 | 3;
    progressPercent: number;
}

const CustomersCopiedBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore } = useContext(RootStoreContext);
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [processUpdatesByCustomerId, setProcessUpdatesByCustomerId] = useState<
        Record<string, ICopyCustomerProcessUpdate>
    >({});
    const isCopyModalRoute = props.location.pathname === Route.customersCopy;

    const queryVariables = useMemo<GetUmsCopyCustomerProcessesOverviewPagedVariables>(() => {
        const normalizedSearchQuery = searchQuery.trim();

        return {
            limit: ITEMS_PER_PAGE,
            offset: (currentPage - 1) * ITEMS_PER_PAGE,
            where: {
                ...(normalizedSearchQuery.length > 0
                    ? {
                          _or: [
                              { name: { _ilike: `%${normalizedSearchQuery}%` } },
                              { number: { _ilike: `%${normalizedSearchQuery}%` } }
                          ]
                      }
                    : {})
            }
        };
    }, [currentPage, searchQuery]);

    const {
        data: processesData,
        previousData: previousProcessesData,
        loading: processesLoading,
        error: processesError,
        refetch: refetchProcesses
    } = useQuery<GetUmsCopyCustomerProcessesOverviewPaged, GetUmsCopyCustomerProcessesOverviewPagedVariables>(
        GET_UMS_COPY_CUSTOMER_PROCESSES_OVERVIEW_PAGED,
        {
            variables: queryVariables,
            fetchPolicy: "network-only"
        }
    );

    const queryProcesses = useMemo(
        () => (processesData ?? previousProcessesData)?.processes ?? [],
        [previousProcessesData, processesData]
    );

    const copiedCustomers = useMemo<ICopiedCustomerListItem[]>(
        () =>
            queryProcesses.map((item) => {
                const processUpdate = processUpdatesByCustomerId[item.customerid];

                return {
                    id: item.customerid,
                    name: item.name,
                    number: item.number,
                    copyStatus: normalizeStatus(processUpdate?.status ?? item.status),
                    copyProgress: normalizeProgress(processUpdate?.progressPercent ?? item.progress_percent),
                    copiedBy: item.username || "-"
                };
            }),
        [processUpdatesByCustomerId, queryProcesses]
    );

    const runningProcessIds = useMemo(
        () => copiedCustomers.filter((item) => item.copyStatus === RUNNING_COPY_STATUS).map((item) => item.id),
        [copiedCustomers]
    );

    const subscriptionVariables = useMemo<SubscribeUmsCopyCustomerProcessesOverviewPagedVariables>(
        () => ({
            where: {
                customerid: { _in: runningProcessIds }
            }
        }),
        [runningProcessIds]
    );

    const { data: subscriptionData } = useSubscription<
        SubscribeUmsCopyCustomerProcessesOverviewPaged,
        SubscribeUmsCopyCustomerProcessesOverviewPagedVariables
    >(
        SUBSCRIBE_UMS_COPY_CUSTOMER_PROCESSES_OVERVIEW_PAGED,
        {
            variables: subscriptionVariables,
            skip: runningProcessIds.length === 0
        }
    );

    useEffect(() => {
        if (!subscriptionData) {
            return;
        }

        setProcessUpdatesByCustomerId((currentUpdates) => {
            let hasChanges = false;
            const nextUpdates = { ...currentUpdates };

            subscriptionData.processes.forEach((process) => {
                const status = normalizeStatus(process.status);
                const progressPercent = normalizeProgress(process.progress_percent);
                const currentUpdate = currentUpdates[process.customerid];

                if (currentUpdate?.status !== status || currentUpdate?.progressPercent !== progressPercent) {
                    hasChanges = true;
                    nextUpdates[process.customerid] = { status, progressPercent };
                }
            });

            return hasChanges ? nextUpdates : currentUpdates;
        });
    }, [subscriptionData]);

    useEffect(() => {
        const updateSearchTimeout = window.setTimeout(() => {
            setSearchQuery(searchInput);
        }, SEARCH_DEBOUNCE_DELAY_MS);

        return () => window.clearTimeout(updateSearchTimeout);
    }, [searchInput]);

    const handleCopiedCustomersSearch = useCallback(({ searchQuery: nextSearchQuery }) => {
        setSearchInput(nextSearchQuery ?? "");
        setCurrentPage(1);
    }, []);

    const copyCustomerButton = useMemo(
        () => (
            <div className={styles.copyCustomerButtonWrapper}>
                <UI.Button
                    label={t("screens.dashboard.copy_customer.title")}
                    to={Route.customersCopy}
                    className="customer-button float-right w-100"
                />
            </div>
        ),
        [t]
    );

    const closeCopyCustomerDialog = useCallback(() => {
        props.history.replace(Route.customersCopied);
        void refetchProcesses();
    }, [props.history, refetchProcesses]);

    useEffect(() => {
        navStore.setTitle(t("screens.dashboard.nav.copied_customers"));
        navStore.setBackbutton(null);

        return () => {
            navStore.setBackbutton(null);
        };
    }, [navStore, t]);

    if (processesLoading && copiedCustomers.length === 0 && !previousProcessesData) {
        return <Screens.Loading />;
    }

    if (processesError) {
        return <Screens.Error message={processesError.message} networkError={processesError.networkError} />;
    }

    return (
        <UI.Container className="pt-4">
            <UI.Row>
                <UI.Col md={12}>
                    <UI.Card title={t("screens.dashboard.copy_customer.processes_title")}>
                        <CopiedCustomerList
                            umsCustomers={copiedCustomers}
                            totalEntries={(processesData ?? previousProcessesData)?.processes_aggregate.aggregate?.count ?? 0}
                            currentPage={currentPage}
                            itemsPerPage={ITEMS_PER_PAGE}
                            onPageChanged={setCurrentPage}
                            onSearch={handleCopiedCustomersSearch}
                            searchAppendElement={copyCustomerButton}
                        />
                    </UI.Card>
                </UI.Col>
            </UI.Row>
            <UI.Modal
                title={t("screens.dashboard.copy_customer.title")}
                show={isCopyModalRoute}
                onClose={closeCopyCustomerDialog}
                size="lg"
            >
                {isCopyModalRoute ? (
                    <CopyCustomer
                        onCloseModal={closeCopyCustomerDialog}
                    />
                ) : null}
            </UI.Modal>
        </UI.Container>
    );
};

const normalizeStatus = (status: number): 1 | 2 | 3 => {
    switch (status) {
        case 1:
            return 1;
        case 2:
            return 2;
        case 3:
            return 3;
        default:
            return 1;
    }
};

const normalizeProgress = (progress: number) => {
    if (Number.isNaN(progress)) {
        return 0;
    }

    return Math.max(0, Math.min(Math.round(progress), 100));
};

export const CustomersCopied = withRouter(Sentry.withProfiler(observer(CustomersCopiedBase)));

